import React, { useState, useEffect, useRef } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import InvoiceQuotationEditable from "./InvoiceQuotationEditable";
import InvoiceQuotation from "./InvoiceQuotation";
import { axiosInstances } from "./apiConfig";

export default function InvoiceQuotationApp({ invoiceId, onCancel }) {
  const blankData = {
    invoiceId: "",
    company: {
      name: "My Company Pvt Ltd",
      address: "123 Main Street",
      city: "Hyderabad",
      state: "Telangana",
      zip: "500001",
      phone: "040-12345678",
      email: "info@mycompany.com",
    },
    customer: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
    },
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    status: "Open",
    items: [{ code: "", description: "", quantity: 0, rate: 0 }],
    igst: 0.18,
    cgst: 0.09,
    sgst: 0.09,
    subTotal: 0,
    grandTotal: 0,
    terms: "",
    authorizedBy: { name: "", position: "", company: "" },
  };

  const [invoiceData, setInvoiceData] = useState(blankData);
  const [isEditing, setIsEditing] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const convertToInputDateFormat = (dateStr) => {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDate = (inputDate) => {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  };

  // 🔹 Fetch Invoice details by Order ID
  useEffect(() => {
    if (!invoiceId) return; // new invoice case
    axiosInstances.invoiceApi.get(`/${invoiceId}`)
      .then((resp) => {
        const data = resp.data.data;
        if (!data) return;

        const invoice = data.invoice || {};
        const customer = data.customer || {};
        const products = data.products || [];
        const prices = data.prices || [];
        // const items = data.items || [];
        const items = products.map((p) => {
          const price = prices.find((pr) => pr.productId === p.id);
          return {
            id: p.id,
            priceId: price?.id || null,
            code: p.id,
            description: p.description,
            quantity: price?.quantity || 1,
            amount: price?.amount || 0,
          };
        });

        setInvoiceData((prev) => ({
          ...prev,
          id: invoice.id,
          orderId: invoice.orderId,
          invoiceNo: invoice.invoiceNo,
          invoiceDate: convertToInputDateFormat(invoice.invoiceDate),
          status: invoice.status || "Open",
          company: {
            ...prev.company,
            ...(invoice.company || {}),
          },
          customer: customer,
          items: items,
          igst: invoice.igst || 0.18,
          cgst: invoice.cgst || 0.09,
          sgst: invoice.sgst || 0.09,
          subTotal: invoice.subTotal || 0,
          grandTotal: invoice.grandTotal || 0,
          terms: invoice.terms || "",
          authorizedBy: invoice.authorizedBy || prev.authorizedBy,
        }));

        console.log("✅ Invoice loaded successfully", invoice);
      })
      .catch((err) => {
        console.error("❌ Error loading invoice:", err);
      });
  }, [invoiceId]);

  const handlePreview = (data) => {
    setInvoiceData(data);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const subTotal = invoiceData.items.reduce(
        (sum, i) => sum + i.quantity * i.amount,
        0
      );
      const grandTotal =
        subTotal + subTotal * (invoiceData.igst + invoiceData.cgst + invoiceData.sgst);

      const payload = {
        ...invoiceData,
        id: invoiceData.id,
        prices: invoiceData.items.map((i) => ({
          id: i.priceId || null,
          productId: i.id,
          quantity: i.quantity,
          amount: i.amount,
          total: i.amount * i.quantity,
        })),
        subTotal,
        grandTotal,
        invoiceDate: formatDate(invoiceData.invoiceDate),
      };

      let resp;
      if (invoiceData.id && invoiceData.id !== 0) {
        resp = await axiosInstances.invoiceApi.post(`/update`, payload);
      } else {
        resp = await axiosInstances.invoiceApi.post(`/save`, payload);
      }

      setInvoiceData((prev) => ({
        ...prev,
        ...(resp.data.data?.invoice || resp.data.data || {}),
      }));

      setSnackbar({
        open: true,
        message: invoiceData.id
          ? "Invoice updated successfully!"
          : "Invoice created successfully!",
        severity: "success",
      });

      if (onCancel) {
        setTimeout(() => onCancel(), 1000);
      }
    } catch (err) {
      console.error("❌ Error saving invoice:", err);
      setSnackbar({
        open: true,
        message: "Failed to save invoice. Check console for details.",
        severity: "error",
      });
    }
  };

  const handleEdit = () => setIsEditing(true);
  const handlePrint = () => window.print();
  const handleCloseSnackbar = () =>
    setSnackbar({ ...snackbar, open: false });

  return (
    <>
      {isEditing ? (
        <InvoiceQuotationEditable
          invoiceData={invoiceData}
          onPreview={handlePreview}
          onCancel={onCancel}
        />
      ) : (
        <InvoiceQuotation
          invoiceData={invoiceData}
          onEdit={handleEdit}
          onPrint={handlePrint}
          onSave={handleSave}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
